import { Alert, Autocomplete, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { D365FormRow, D365TextField, d365ControlSx } from '@/components/D365FormControls';
import { RecordFormLayout } from '@/components/RecordFormLayout';
import {
  resolveAddress,
  searchAddresses,
  type AddressSuggestion,
} from '@/features/addresses/api/addresses';
import {
  createClientServiceLocation,
  type ServiceLocationInput,
} from '@/features/clients/api/clients';

const empty: ServiceLocationInput = {
  name: '',
  gateCode: '',
  streetNumber: '',
  route: '',
  locality: '',
  postalCode: '',
  latitude: 0,
  longitude: 0,
};

export function ServiceLocationFormPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const clientId = Number(useParams().clientId);
  const [v, setV] = useState(empty);
  const [addressQuery, setAddressQuery] = useState('');
  const [debouncedAddressQuery, setDebouncedAddressQuery] = useState('');
  const [usingManualAddress, setUsingManualAddress] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAddressQuery(addressQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [addressQuery]);

  const suggestions = useQuery({
    queryKey: ['address-search', debouncedAddressQuery],
    queryFn: () => searchAddresses(debouncedAddressQuery),
    enabled: debouncedAddressQuery.length >= 3,
    staleTime: 60_000,
  });
  const selectedAddress = useMutation({
    mutationFn: (suggestion: AddressSuggestion) => resolveAddress(suggestion.udprn),
    onSuccess: (address) => {
      const building = [address.building_name, address.building_number].filter(Boolean).join(' ');
      setV((current) => ({
        ...current,
        streetNumber: building || address.address_line_1,
        route: address.thoroughfare || address.address_line_2,
        locality: address.dependent_locality || address.post_town,
        postalCode: address.postcode,
        latitude: address.latitude,
        longitude: address.longitude,
      }));
    },
  });
  const m = useMutation({
    mutationFn: () =>
      createClientServiceLocation(clientId, {
        ...v,
        name: locationName(v),
      }),
    onSuccess: async (id) => {
      await qc.invalidateQueries({ queryKey: ['clients', clientId, 'locations'] });
      void nav(`/clients/${clientId}/service-locations/${id}`);
    },
  });
  const field = (k: keyof ServiceLocationInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((x) => ({
      ...x,
      [k]: k === 'latitude' || k === 'longitude' ? Number(e.target.value) : e.target.value,
      ...(isAddressTextField(k) ? { latitude: 0, longitude: 0 } : {}),
    }));
  const useManualAddress = () => {
    const enteredAddress = addressQuery.trim();
    if (!enteredAddress) return;
    setUsingManualAddress(true);
    setV((current) => ({
      ...current,
      streetNumber: enteredAddress,
      route: '',
      locality: '',
      postalCode: '',
      latitude: 0,
      longitude: 0,
    }));
  };

  return (
    <RecordFormLayout
      title="New Service Location"
      type="Service location"
      saving={m.isPending}
      disabled={!hasAddress(v)}
      dirty={JSON.stringify(v) !== JSON.stringify(empty)}
      error={m.isError}
      onBack={() => void nav(`/clients/${clientId}`)}
      onSave={() => m.mutate()}
    >
      <Stack spacing={2}>
        <D365TextField label="Gate / access code" value={v.gateCode} onChange={field('gateCode')} />
        <D365FormRow label="Find address">
          <Autocomplete<AddressSuggestion, false, false, false>
            options={suggestions.data ?? []}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.suggestion}
            isOptionEqualToValue={(option, value) => option.udprn === value.udprn}
            loading={suggestions.isFetching || selectedAddress.isPending}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input' || reason === 'clear') setAddressQuery(value);
            }}
            onChange={(_event, value) => {
              if (value) selectedAddress.mutate(value);
            }}
            noOptionsText={
              addressQuery.trim().length < 3 ? 'Enter at least 3 characters' : 'No addresses found'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Find address"
                placeholder="Start typing an address or postcode"
                sx={d365ControlSx}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(suggestions.isFetching || selectedAddress.isPending) && (
                          <CircularProgress color="inherit" size={18} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </D365FormRow>
        {addressQuery.trim() && (
          <D365FormRow label="Address not listed?">
            <Button variant="outlined" onClick={useManualAddress}>
              Use entered address manually
            </Button>
          </D365FormRow>
        )}
        {(suggestions.isError || selectedAddress.isError) && (
          <Alert severity="error">
            Address lookup is unavailable. You can still enter the address manually.
          </Alert>
        )}
        {usingManualAddress && (
          <Alert severity="info">
            The entered address has been copied below. Split or correct the fields if needed, then
            save. No coordinates from a different property will be retained; directions will use the
            written address.
          </Alert>
        )}
        <D365TextField
          label="Building number or name"
          value={v.streetNumber}
          onChange={field('streetNumber')}
        />
        <D365TextField label="Street" value={v.route} onChange={field('route')} />
        <D365TextField label="Town / city" value={v.locality} onChange={field('locality')} />
        <D365TextField label="Postcode" value={v.postalCode} onChange={field('postalCode')} />
        <Alert severity="info">
          Coordinates are filled automatically by address lookup and power route maps and
          directions.
        </Alert>
        <D365TextField
          type="number"
          label="Latitude"
          value={v.latitude}
          onChange={field('latitude')}
        />
        <D365TextField
          type="number"
          label="Longitude"
          value={v.longitude}
          onChange={field('longitude')}
        />
      </Stack>
    </RecordFormLayout>
  );
}

function isAddressTextField(key: keyof ServiceLocationInput) {
  return ['streetNumber', 'route', 'locality', 'postalCode'].includes(key);
}

function hasAddress(value: ServiceLocationInput) {
  return Boolean(
    value.streetNumber.trim() ||
    value.route.trim() ||
    value.locality.trim() ||
    value.postalCode.trim(),
  );
}

function locationName(value: ServiceLocationInput) {
  return (
    [value.streetNumber, value.route]
      .filter((part) => part.trim())
      .join(' ')
      .trim() ||
    value.locality.trim() ||
    value.postalCode.trim() ||
    'Service location'
  );
}
